import { offsetAt } from './portalTime';
import {
  ANSWER_COMMENTS,
  ANSWER_CONFIRM_CHECKBOX,
  ANSWER_DATE,
  ANSWER_MEAL_WAIVER,
  ANSWER_YES_NO,
  PortalTimesheetAnswer,
  PortalTimesheetQuestion,
  PortalTimesheetQuestionChild,
  PortalTimesheetSubmitBody,
} from './portalTypes';

/**
 * Employee Time Portal — the weekly submit's pure rules (P-TAS-20 Phase 3, App-2): which
 * timesheet questions show, when they are complete, the exact answer strings, the typed
 * signature's name match, and the POST /portal/timesheet body. Every string matches what
 * the mobile app stores (MobileApp src/pages/submit/components/*), because payroll, the
 * timecard PDF and the exception triggers read TimesheetAnswers.Answer as text.
 *
 *   YesNo (1)                 "Yes" | "No"; children shown when the answer matches their trigger
 *   Date (2)                  "MM-DD-YYYY"
 *   ConfirmCheckbox (3)       "Yes" (must be checked) — "No" is sent only if it isn't
 *   MealConditionalWaiver (4) "Yes - " + waiverDates.join(', ') — Yes only, as on mobile (its
 *                             validate() requires Yes); hidden when there are no dates
 *   Comments (5)              trimmed text (≤ 150), omitted when blank; required only by requiredWhen
 */

/** Per question ID: 'Yes' / 'No' (YesNo, ConfirmCheckbox, meal waiver), `YYYY-MM-DD` (Date), text (Comments). */
export type AnswerState = Record<number, string | null | undefined>;

export const COMMENT_MAX = 150;
/** mobile YesNoQuestion DEFAULT_ANSWER_TRIGGER. */
const DEFAULT_TRIGGER = 'Yes';

/** Mobile answersMatch: trimmed, case-insensitive. */
export function answersMatch(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/** The questions to show, in order. A meal-waiver question only when the week has waiver dates (mobile Submit.tsx). */
export function visibleQuestions(questions: PortalTimesheetQuestion[], waiverDates: string[]): PortalTimesheetQuestion[] {
  return [...questions]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .filter((q) => q.answerType !== ANSWER_MEAL_WAIVER || waiverDates.length > 0);
}

/**
 * My Timesheet's questions card after submit: the questions that were answered (parents
 * and their shown children), in question order, each with its stored answer text.
 * Children follow their parent. Null when the answers aren't known (an older API).
 */
export function submittedAnswers(
  questions: PortalTimesheetQuestion[],
  answers: PortalTimesheetAnswer[] | undefined,
): Array<{ id: number; question: string; answer: string; child: boolean }> | null {
  if (!answers) return null;
  const byID = new Map(answers.map((a) => [a.timesheetQuestionID, (a.answer ?? '').trim()]));
  const out: Array<{ id: number; question: string; answer: string; child: boolean }> = [];
  for (const q of [...questions].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))) {
    if (byID.has(q.timesheetQuestionID)) {
      out.push({ id: q.timesheetQuestionID, question: q.question, answer: byID.get(q.timesheetQuestionID) || '—', child: false });
    }
    for (const c of [...(q.children ?? [])].sort((x, y) => (x.order ?? 0) - (y.order ?? 0))) {
      if (byID.has(c.timesheetQuestionID)) {
        out.push({ id: c.timesheetQuestionID, question: c.question, answer: byID.get(c.timesheetQuestionID) || '—', child: true });
      }
    }
  }
  return out;
}

/** A YesNo parent's children whose trigger matches its answer (mobile isChildTriggered). Only YesNo parents have shown children. */
export function triggeredChildren(q: PortalTimesheetQuestion, answers: AnswerState): PortalTimesheetQuestionChild[] {
  if (q.answerType !== ANSWER_YES_NO) return [];
  const a = answers[q.timesheetQuestionID];
  if (a !== 'Yes' && a !== 'No') return [];
  return [...(q.children ?? [])]
    .sort((x, y) => (x.order ?? 0) - (y.order ?? 0))
    .filter((c) => c.answerType !== ANSWER_MEAL_WAIVER)
    .filter((c) => answersMatch(c.answerTrigger || DEFAULT_TRIGGER, a));
}

/** requiredWhen: required while question `requiredWhenQuestionID` holds `requiredWhenAnswer`. */
export function isRequiredByAnswer(q: Pick<PortalTimesheetQuestion, 'requiredWhenQuestionID' | 'requiredWhenAnswer'>, answers: AnswerState): boolean {
  if (q.requiredWhenQuestionID == null) return false;
  return answersMatch(answers[q.requiredWhenQuestionID] ?? null, q.requiredWhenAnswer);
}

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Is one question answered well enough to submit (mobile validate())? Comments are optional
 * unless `required` (top-level requiredWhen); a child's Comments are always optional.
 */
export function isAnswered(answerType: number, value: string | null | undefined, required: boolean): boolean {
  switch (answerType) {
    case ANSWER_YES_NO:
      return value === 'Yes' || value === 'No';
    case ANSWER_MEAL_WAIVER:
    case ANSWER_CONFIRM_CHECKBOX:
      return value === 'Yes';
    case ANSWER_DATE:
      return !!value && YMD_RE.test(value);
    case ANSWER_COMMENTS:
      return required ? !!value && value.trim().length > 0 : true;
    default:
      return true;
  }
}

/** The IDs still needing an answer (top-level questions and their triggered children). Empty = ready. */
export function unansweredQuestionIDs(questions: PortalTimesheetQuestion[], answers: AnswerState): number[] {
  const missing: number[] = [];
  for (const q of questions) {
    const required = q.answerType === ANSWER_COMMENTS && isRequiredByAnswer(q, answers);
    if (!isAnswered(q.answerType, answers[q.timesheetQuestionID], required)) missing.push(q.timesheetQuestionID);
    for (const c of triggeredChildren(q, answers)) {
      if (!isAnswered(c.answerType, answers[c.timesheetQuestionID], false)) missing.push(c.timesheetQuestionID);
    }
  }
  return missing;
}

/** Mobile MealConditionalWaiver.getAnswers for the only answer it accepts (Yes) — exactly. */
export function mealWaiverAnswer(waiverDates: string[]): string {
  return 'Yes - ' + waiverDates.join(', ');
}

/** `YYYY-MM-DD` → `MM-DD-YYYY` (mobile DateQuestion). */
export function formatDateAnswer(ymd: string): string {
  const [y, m, d] = ymd.split('-');
  return `${m}-${d}-${y}`;
}

/** Mobile formatQuestionText: `${dates}` becomes the waiver dates. */
export function questionText(text: string, waiverDates: string[]): string {
  if (!text) return '';
  // eslint-disable-next-line no-template-curly-in-string -- the literal placeholder mobile replaces
  return waiverDates.length ? text.replace('${dates}', waiverDates.join(', ')) : text.replace('{dates}', 'none');
}

/** One question's stored answer, or null when nothing is sent for it. */
export function answerString(answerType: number, value: string | null | undefined, waiverDates: string[]): string | null {
  switch (answerType) {
    case ANSWER_YES_NO:
      return value === 'Yes' || value === 'No' ? value : null;
    case ANSWER_CONFIRM_CHECKBOX:
      return value === 'Yes' ? 'Yes' : 'No';
    case ANSWER_MEAL_WAIVER:
      return value === 'Yes' ? mealWaiverAnswer(waiverDates) : null;
    case ANSWER_DATE:
      return value && YMD_RE.test(value) ? formatDateAnswer(value) : null;
    case ANSWER_COMMENTS: {
      const t = (value ?? '').trim().slice(0, COMMENT_MAX);
      return t ? t : null;
    }
    default:
      return null;
  }
}

/** The `timesheetAnswers` array: each question, then the children its answer shows (mobile order). */
export function buildTimesheetAnswers(questions: PortalTimesheetQuestion[], answers: AnswerState, waiverDates: string[]): PortalTimesheetAnswer[] {
  const out: PortalTimesheetAnswer[] = [];
  for (const q of questions) {
    const a = answerString(q.answerType, answers[q.timesheetQuestionID], waiverDates);
    if (a != null) out.push({ timesheetQuestionID: q.timesheetQuestionID, answer: a });
    for (const c of triggeredChildren(q, answers)) {
      const ca = answerString(c.answerType, answers[c.timesheetQuestionID], waiverDates);
      if (ca != null) out.push({ timesheetQuestionID: c.timesheetQuestionID, answer: ca });
    }
  }
  return out;
}

/* ------------------------------------------------------------------ signature */

/** Trimmed, inner spaces collapsed, lower-case. */
export function normalizeName(value: string): string {
  return (value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}

export interface PersonName {
  first: string;
  middle?: string | null;
  last: string;
}

/** `First Last` — the name the employee is asked to type. */
export function displayName(name: PersonName): string {
  return [name.first, name.last].map((p) => (p ?? '').trim()).filter(Boolean).join(' ');
}

/** Whether the typed name is the employee's (`First Last`, or with the middle name), case- and space-insensitive. */
export function signatureMatches(typed: string, name: PersonName): boolean {
  const t = normalizeName(typed);
  if (!t) return false;
  const short = normalizeName(displayName(name));
  const full = normalizeName([name.first, name.middle ?? '', name.last].join(' '));
  return t === short || t === full;
}

/* ------------------------------------------------------------------ the submit body */

/**
 * POST /portal/timesheet — the body mobile sends (TimesheetManager.submitTimesheet), with
 * every ID array present. The portal has no task / sick-claim / drive-time / work-order
 * claims, so those arrays are empty; the TimeEntry and TimeEntryHourly IDs must be the
 * week's exact set (the API refuses a mismatch). submittedDate is the instant, its offset
 * the tenant zone's.
 */
export function buildSubmitBody(args: {
  employeeID: number;
  payPeriodID: number;
  nowMs: number;
  zone: string;
  timesheetAnswers: PortalTimesheetAnswer[];
  signature: string;
  timeEntryIDs: number[];
  timeEntryHourlyIDs: number[];
}): PortalTimesheetSubmitBody {
  return {
    employeeID: args.employeeID,
    payPeriodID: args.payPeriodID,
    submittedDate: new Date(args.nowMs).toISOString(),
    submittedDateOffset: offsetAt(args.nowMs, args.zone),
    timesheetAnswers: args.timesheetAnswers,
    signature: args.signature,
    timeEntryIDs: [...args.timeEntryIDs],
    employeeTaskIDs: [],
    miscEmployeeTaskIDs: [],
    timeEntryHourlyIDs: [...args.timeEntryHourlyIDs],
    employeeOperationalTaskIDs: [],
    employeeCustomerServiceWarrantyTaskIDs: [],
    employeeSickClaimIDs: [],
    driveTimeEntryIDs: [],
    employeeWorkOrderTaskIDs: [],
  };
}
